import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Avatar from '@material-ui/core/Avatar'
import TextField from '@material-ui/core/TextField'
import CheckIcon from 'components/material-symbol/icons/Check'
import { getInitials } from 'lib/text-display'
import useFieldStyles from 'pages/things-to-do/events/register/registration-with-payment-intents/redesign/styles'
import useStyles from './styles'

const matchesSearch = (student, term) => {
  const needle = term.toLowerCase().trim()
  if (!needle) return true
  return (
    `${student.firstName} ${student.lastName}`.toLowerCase().includes(needle) ||
    student.firstName.toLowerCase().includes(needle) ||
    student.lastName.toLowerCase().includes(needle)
  )
}

// Radio list of existing students. Native radios keep arrow-key navigation and
// the checked state; the row is just their visible face. `searchable` adds the
// filter coaches use for long rosters.
const StudentPicker = ({
  students,
  selectedId,
  onSelect,
  name,
  label,
  searchable,
  emptyMessage,
}) => {
  const classes = useStyles()
  const fieldClasses = useFieldStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const visible = students.filter(student => matchesSearch(student, searchTerm))

  return (
    <div className={classes.sectionGap}>
      {searchable && (
        <div className={clsx(fieldClasses.fieldGrid, classes.fields)}>
          <TextField
            id={`${name}-search`}
            label="Search students"
            type="search"
            name="studentSearch"
            autoComplete="off"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            className={clsx(fieldClasses.fieldFull, classes.searchField)}
            InputLabelProps={{ shrink: true }}
            placeholder="Search by first or last name"
          />
        </div>
      )}
      {visible.length > 0 ? (
        <div
          role="radiogroup"
          aria-label={label}
          className={classes.pickerList}
        >
          {visible.map(student => {
            const selected = String(selectedId) === String(student.id)
            const fullName = `${student.firstName} ${student.lastName}`
            return (
              <label
                key={student.id}
                className={clsx(
                  classes.pickerOption,
                  selected && classes.pickerOptionSelected,
                )}
                data-testid="student-card"
              >
                <input
                  type="radio"
                  name={name}
                  value={student.id}
                  checked={selected}
                  onChange={() => onSelect(student.id)}
                  className={classes.pickerInput}
                  aria-label={fullName}
                />
                <Avatar className={classes.pickerAvatar} aria-hidden="true">
                  {getInitials(fullName)}
                </Avatar>
                <span>
                  <span className={classes.pickerName}>{fullName}</span>
                  <br />
                  <span className={classes.pickerCaption}>
                    {student.isMinor ? 'Junior' : 'Adult'}
                  </span>
                </span>
                {selected && (
                  <CheckIcon
                    className={classes.pickerCheck}
                    aria-hidden="true"
                  />
                )}
              </label>
            )
          })}
        </div>
      ) : (
        <p className={classes.emptyNote}>
          {searchTerm ? 'No students match your search.' : emptyMessage}
        </p>
      )}
    </div>
  )
}

StudentPicker.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      isMinor: PropTypes.bool,
    }),
  ).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  searchable: PropTypes.bool,
  emptyMessage: PropTypes.string,
}

export default StudentPicker
